import { useState } from 'react';
import { ChevronLeft, ChevronRight, FileText } from 'lucide-react';
import type { ChPost } from '../types';

interface Props {
  posts: ChPost[];
  onPostClick: (post: ChPost) => void;
}

function getDaysInMonth(year: number, month: number): number {
  return new Date(year, month + 1, 0).getDate();
}

function getFirstDayOfWeek(year: number, month: number): number {
  const day = new Date(year, month, 1).getDay();
  return day === 0 ? 6 : day - 1;
}

const WEEKDAYS = ['Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb', 'Dom'];
const MONTHS_PT = [
  'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
  'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro',
];

export function PostCalendar({ posts, onPostClick }: Props) {
  const today = new Date();
  const [year, setYear] = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth());

  const daysInMonth = getDaysInMonth(year, month);
  const firstDay = getFirstDayOfWeek(year, month);

  function prevMonth() {
    if (month === 0) {
      setMonth(11);
      setYear(y => y - 1);
    } else {
      setMonth(m => m - 1);
    }
  }

  function nextMonth() {
    if (month === 11) {
      setMonth(0);
      setYear(y => y + 1);
    } else {
      setMonth(m => m + 1);
    }
  }

  function getPostsForDay(day: number): ChPost[] {
    const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    return posts.filter(p => p.scheduled_date?.startsWith(dateStr));
  }

  const isToday = (day: number) =>
    today.getFullYear() === year && today.getMonth() === month && today.getDate() === day;

  const cells: (number | null)[] = [];
  for (let i = 0; i < firstDay; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);

  return (
    <div className="ch-card p-0 overflow-hidden">
      <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
        <button onClick={prevMonth} className="p-2 rounded-xl hover:bg-gray-100 transition-colors">
          <ChevronLeft className="w-4 h-4" />
        </button>
        <h3 className="text-sm font-bold text-[var(--ch-ink)]">
          {MONTHS_PT[month]} {year}
        </h3>
        <button onClick={nextMonth} className="p-2 rounded-xl hover:bg-gray-100 transition-colors">
          <ChevronRight className="w-4 h-4" />
        </button>
      </div>

      <div className="grid grid-cols-7">
        {WEEKDAYS.map(day => (
          <div key={day} className="px-1 py-2 text-center text-[10px] font-semibold text-gray-400 uppercase">
            {day}
          </div>
        ))}
      </div>

      <div className="grid grid-cols-7 border-t border-gray-100">
        {cells.map((day, idx) => {
          if (day === null) {
            return <div key={`empty-${idx}`} className="min-h-[80px] md:min-h-[100px] border-b border-r border-gray-50" />;
          }

          const dayPosts = getPostsForDay(day);
          return (
            <div
              key={day}
              className={`min-h-[80px] md:min-h-[100px] border-b border-r border-gray-50 p-1 ${
                isToday(day) ? 'bg-[var(--ch-lime)]/5' : ''
              }`}
            >
              <span className={`inline-flex items-center justify-center w-6 h-6 text-xs font-medium rounded-full ${
                isToday(day) ? 'bg-[var(--ch-ink)] text-white' : 'text-gray-500'
              }`}>
                {day}
              </span>
              <div className="mt-0.5 space-y-0.5">
                {dayPosts.slice(0, 3).map(post => (
                  <button
                    key={post.id}
                    onClick={() => onPostClick(post)}
                    className={`w-full text-left text-[10px] font-medium px-1.5 py-0.5 rounded truncate transition-opacity hover:opacity-80 ch-badge-${post.status}`}
                  >
                    {post.title}
                  </button>
                ))}
                {dayPosts.length > 3 && (
                  <span className="text-[10px] text-gray-400 px-1.5">
                    +{dayPosts.length - 3} mais
                  </span>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {posts.filter(p => !p.scheduled_date).length > 0 && (
        <div className="px-5 py-3 border-t border-gray-100 bg-gray-50">
          <p className="text-xs font-semibold text-gray-500 mb-2">
            <FileText className="w-3 h-3 inline mr-1" />
            Sem data ({posts.filter(p => !p.scheduled_date).length})
          </p>
          <div className="flex gap-1.5 flex-wrap">
            {posts.filter(p => !p.scheduled_date).slice(0, 5).map(post => (
              <button
                key={post.id}
                onClick={() => onPostClick(post)}
                className="ch-chip text-[10px] hover:bg-gray-200 transition-colors"
              >
                {post.title}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
